import { fabric } from "fabric";
import { PlaceholderMapping, UpdateTask } from "./types";
import { ensureFabricVideoRegistered } from "../video";
import { ensureFabricModel3dRegistered } from "../model3d";
import { CANVAS_OBJECT_CONTROL_CONFIG } from "../../../helper/canvasConfig";

const INTERACTION_PROP_KEYS = [
  "selectable",
  "evented",
  "hasControls",
  "hasBorders",
  "lockMovementX",
  "lockMovementY",
  "lockScalingX",
  "lockScalingY",
  "lockSkewingX",
  "lockSkewingY",
  "lockRotation",
  "lockScalingFlip",
  "borderColor",
  "cornerSize",
  "transparentCorners",
  "hoverCursor"
] as const;

export const getPlaceholderInteractionOverrides = (placeholder: fabric.Object): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};
  for (const key of INTERACTION_PROP_KEYS) {
    const value = (placeholder as any)[key];
    if (value !== undefined) {
      overrides[key] = value;
    }
  }
  return overrides;
};

/**
 * 解析图片替换后应使用的交互属性。
 *
 * - 分享只读：占位符已被 lockMovementX=true 锁定 → 继承占位符只读属性，避免替换瞬间可编辑
 * - 编辑态渐进加载：占位符使用 placeholder 配置（禁缩放/无控制点）→ 必须恢复为 normal，
 *   否则真实图片会继承 lockScalingX/Y，出现「看得到控制点但拖不动缩放」或完全无法缩放
 */
export const resolveImageInteractionProps = (placeholder: fabric.Object): Record<string, unknown> => {
  const isShareReadonly = (placeholder as any).lockMovementX === true;
  if (isShareReadonly) {
    return getPlaceholderInteractionOverrides(placeholder);
  }
  return { ...CANVAS_OBJECT_CONTROL_CONFIG.normal };
};

export class RenderScheduler {
  private updateQueue: UpdateTask[] = [];
  private isUpdateScheduled: boolean = false;
  private readonly UPDATE_TIME_BUDGET: number = 12; // 每帧更新的时间预算(ms)
  private canvas: fabric.Canvas | null = null;

  // 【埋点支持】空闲回调函数，在渲染队列为空时触发
  private onIdleCallback: (() => void) | null = null;

  constructor() {}

  public setCanvas(canvas: fabric.Canvas) {
    this.canvas = canvas;
  }

  /**
   * 设置空闲回调函数
   * 【埋点支持】当渲染队列为空时触发回调
   */
  public setOnIdleCallback(callback: (() => void) | null) {
    this.onIdleCallback = callback;
  }

  public clear() {
    this.updateQueue = [];
    this.isUpdateScheduled = false;
  }

  /**
   * 检查渲染调度器是否空闲
   * 【埋点支持】用于判断所有图片渲染是否完成
   */
  public isIdle(): boolean {
    return this.updateQueue.length === 0 && !this.isUpdateScheduled;
  }

  public schedule(mappings: PlaceholderMapping[], imgElem: HTMLImageElement) {
    this.updateQueue.push({
      mappings,
      imgElem
    });
    this.triggerUpdate();
  }

  private triggerUpdate() {
    if (this.isUpdateScheduled) return;
    this.isUpdateScheduled = true;

    requestAnimationFrame(() => {
      this.processUpdateQueue();
    });
  }

  /**
   * 处理更新队列
   * 使用 requestAnimationFrame 分片执行，每帧限制执行时间
   */
  private processUpdateQueue() {
    this.isUpdateScheduled = false;

    if (!this.canvas || this.updateQueue.length === 0) return;

    const startTime = performance.now();
    let processedCount = 0;

    // 与 performBatchCanvasOperation 保持一致：禁用自动渲染 + 禁用状态跟踪，减少 Fabric 事件触发
    const wasRenderOnAddRemove = (this.canvas as any).renderOnAddRemove;
    const wasStateful = (this.canvas as any).stateful;
    if (this.canvas) {
      (this.canvas as any).renderOnAddRemove = false;
      (this.canvas as any).stateful = false;
    }

    try {
      while (this.updateQueue.length > 0) {
        // 检查时间预算，避免阻塞 UI
        if (performance.now() - startTime > this.UPDATE_TIME_BUDGET) {
          // 如果超时，调度下一帧继续处理
          this.triggerUpdate();
          break;
        }

        const task = this.updateQueue.shift();
        if (task) {
          this.applyImageToCanvas(task.mappings, task.imgElem);
          processedCount++;
        }
      }
    } catch (error) {
      console.error("[ImageLoader] Error processing update queue:", error);
    } finally {
      // 恢复渲染配置（与 performBatchCanvasOperation 对称）
      if (this.canvas) {
        (this.canvas as any).renderOnAddRemove = wasRenderOnAddRemove;
        (this.canvas as any).stateful = wasStateful;

        // 如果有处理任何任务，请求重绘
        if (processedCount > 0) {
          this.canvas.requestRenderAll();
        }
      }

      // 【埋点支持】如果队列为空且没有待调度的更新，触发空闲回调
      if (this.updateQueue.length === 0 && !this.isUpdateScheduled && this.onIdleCallback) {
        this.onIdleCallback();
      }
    }
  }

  /** 占位符相关属性，创建真实图片时需要排除 */
  private static readonly PLACEHOLDER_KEYS = [
    'isPlaceholder',
    'placeholderType',
    'placeholderStatus',
    'placeholderData',
    'isReplacing',
    '__skipHistory',
    '__splitParentClientId',
    '__fitToPlaceholder',
    '__suppressAutoSelect',
    '_preserveGlobalReferenceAnchor',
    'isEnhancingPromptPlaceholder',
  ];

  /**
   * 将加载完成的图片应用到画布
   * 多选时需维护选中状态，避免 remove 导致 Fabric 内部状态不一致引发闪烁
   */
  private applyImageToCanvas(mappings: PlaceholderMapping[], imgElem: HTMLImageElement) {
    const canvas = this.canvas;
    if (!canvas) return;

    // 循环前保存选中
    const active = canvas.getActiveObject();
    const hadSelection = !!active;
    let trackedObjs: fabric.Object[] =
      hadSelection && (active as any).type === "activeSelection"
        ? (active as fabric.ActiveSelection).getObjects().slice()
        : hadSelection
          ? [active as fabric.Object]
          : [];

    // 多选时：先清除选中，避免每次 remove 触发 Fabric 内部 selection 更新导致多次 selection:updated 和重绘
    const placeholdersSet = new Set(mappings.map((m) => m.placeholder));
    const selectionInvolvesPlaceholders =
      hadSelection && trackedObjs.some((o) => placeholdersSet.has(o));
    if (selectionInvolvesPlaceholders) {
      canvas.discardActiveObject();
    }

    mappings.forEach(({ placeholder, originalObj }) => {
      if (!canvas.contains(placeholder)) return;

      const isVideo = originalObj?.type === "video";
      const isModel3d = originalObj?.type === "model3d";
      if (isVideo) ensureFabricVideoRegistered();
      if (isModel3d) ensureFabricModel3dRegistered();
      const Klass: any = isVideo
        ? (fabric as any).Video || fabric.Image
        : isModel3d
          ? (fabric as any).Model3d || fabric.Image
          : fabric.Image;

      // 过滤掉占位符相关属性，避免真实图片被误识别为占位符
      // 【修复】PSD 类型需要保留 isPlaceholder 和 placeholderType，确保工具栏 filter 能正确排除
      const isPsd = originalObj?.placeholderType === 'psd' ||
        originalObj?.assetType === 'psd' ||
        Boolean(originalObj?.psdDownloadUrl);

      const filteredOriginalObj: Record<string, any> = {};
      for (const key of Object.keys(originalObj || {})) {
        // PSD 类型保留 isPlaceholder 和 placeholderType
        if (isPsd && (key === 'isPlaceholder' || key === 'placeholderType')) {
          filteredOriginalObj[key] = originalObj[key];
        } else if (!RenderScheduler.PLACEHOLDER_KEYS.includes(key)) {
          filteredOriginalObj[key] = originalObj[key];
        }
      }

      const imgInstance = new Klass(imgElem, {
        ...filteredOriginalObj,
        left: originalObj.left,
        top: originalObj.top,
        scaleX: originalObj.scaleX,
        scaleY: originalObj.scaleY,
        angle: originalObj.angle,
        flipX: originalObj.flipX,
        flipY: originalObj.flipY,
        originX: originalObj.originX,
        originY: originalObj.originY,
        // 业务规则：编辑态骨架屏的禁缩放属性不能带到真实图片上
        ...resolveImageInteractionProps(placeholder)
      });
      if (isVideo) {
        (imgInstance as any).type = "video";
      } else if (isModel3d) {
        (imgInstance as any).type = "model3d";
      }

      // 标记为跳过历史记录
      (imgInstance as any).__skipHistory = true;

      // 替换占位符
      const idx = canvas.getObjects().indexOf(placeholder);
      if (idx > -1) {
        canvas.insertAt(imgInstance, idx, false);
        canvas.remove(placeholder);

        // 多选时显式恢复选中，避免 Fabric remove 后内部状态不一致导致闪烁
        if (trackedObjs.indexOf(placeholder) >= 0) {
          trackedObjs = trackedObjs.map((o) => (o === placeholder ? imgInstance : o));
        }

        // 延迟删除标记
        setTimeout(() => {
          delete (imgInstance as any).__skipHistory;
        }, 0);
      }
    });

    // 循环结束后统一恢复选中，仅触发一次 selection:updated
    if (hadSelection && active) {
      const validObjs = trackedObjs.filter((o) => canvas.contains(o));
      if (validObjs.length > 0) {
        const selectionToRestore =
          validObjs.length === 1 ? validObjs[0]! : new fabric.ActiveSelection(validObjs, { canvas });
        canvas.setActiveObject(selectionToRestore);
        selectionToRestore.setCoords?.();
      }
    }
  }
}
